import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import { Icon, IconName, DeleteIcon } from '@ardoq/icons';
import { Select } from '@ardoq/select';
import { Checkbox, TextInput } from '@ardoq/forms';
import { FlexBox, Stack } from '@ardoq/layout';
import { GhostButton, DragButton } from '@ardoq/button';
import { LabelFormattingComponentProperties } from '../../types';
import { perspectivesRowDragAndDropHandlers } from '@ardoq/perspectives';
import styled from 'styled-components';
import { spacing, colors, radius, dashedGrey1 } from '@ardoq/design-tokens';
import { header4 } from '@ardoq/typography';
import { WarningNotification } from '@ardoq/status-ui';
import { FieldsSelect } from './FieldsSelect';
import {
  getSelectedLabelFieldOptions,
  getLabelFormattingTypeLabel,
} from './utils';
import { disabledCSS } from '@ardoq/style-helpers';

const TypeLabel = styled.span`
  ${header4}
`;
const RuleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: ${spacing.s16};
  border: ${dashedGrey1};
  border-radius: ${radius.r8};
  padding: ${spacing.s16};
`;

const TypeInputContainer = styled.div`
  flex: 0 1 250px;
`;

const DisableContainer = styled(Stack).attrs({
  gap: 'medium',
  flex: 1,
})<{
  $isDisabled: boolean;
}>`
  ${props => props.$isDisabled && disabledCSS}
`;

const LabelFormattingComponentHorizontal = ({
  labelFormatting,
  onLabelFormattingDeleted,
  onUpdateLabelFormattingAt,
  isComponentFormatting,
  isSelectedTypeValid,
  typeOptions,
  fieldOptions,
  isDisabled,
  index,
  disabledCheckboxMessage,
}: LabelFormattingComponentProperties) => {
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(
      labelFormatting.__id,
      useDragAndDropContext()
    );
  const selectedOptions = getSelectedLabelFieldOptions(
    labelFormatting,
    fieldOptions
  );
  const hasFieldsWarning =
    selectedOptions.length > 4 && labelFormatting.fields.length > 4;

  const selectTypePlaceholder = isComponentFormatting
    ? 'Select component type'
    : 'Select reference type';

  return (
    <RuleContainer
      data-testid="group-box-formatting-rule-by-tag"
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      {!isSelectedTypeValid ? (
        <WarningNotification>
          This label can&apos;t be applied to the viewpoint.
        </WarningNotification>
      ) : null}
      <FlexBox gap="medium" align="center">
        <DragButton draggable />
        <DisableContainer $isDisabled={isDisabled}>
          {isComponentFormatting ? (
            <FlexBox align="center" gap="xsmall">
              <Icon color={colors.icon} iconName={IconName.COMPONENT} />
              <TypeLabel>Component label</TypeLabel>
            </FlexBox>
          ) : (
            <FlexBox align="center" gap="xsmall">
              <Icon color={colors.icon} iconName={IconName.REFERENCE} />
              <TypeLabel> Reference label</TypeLabel>
            </FlexBox>
          )}
          <FlexBox gap="small" align="center">
            <TypeInputContainer>
              {isDisabled ? (
                <TextInput
                  value={
                    getLabelFormattingTypeLabel(labelFormatting, typeOptions) ||
                    selectTypePlaceholder
                  }
                />
              ) : (
                <Select
                  menuPortalTarget={null}
                  placeholder={selectTypePlaceholder}
                  value={labelFormatting.typeName}
                  options={typeOptions}
                  isClearable={false}
                  onChange={option =>
                    onUpdateLabelFormattingAt(index, {
                      typeName: option?.value,
                    })
                  }
                  allowNullOptionValue
                />
              )}
            </TypeInputContainer>
            <div
              style={{
                flex: 1,
                marginBottom: !isDisabled && hasFieldsWarning ? '-24px' : 0,
              }}
            >
              <FieldsSelect
                index={index}
                fieldOptions={fieldOptions}
                selectedOptions={selectedOptions}
                hasFieldsWarning={hasFieldsWarning}
                onUpdateLabelFormattingAt={onUpdateLabelFormattingAt}
                isDisabled={isDisabled}
              />
            </div>
          </FlexBox>
          {isDisabled ? null : (
            <FlexBox align="center">
              <Checkbox
                isChecked={labelFormatting.showFieldName}
                popoverContent={disabledCheckboxMessage}
                isDisabled={Boolean(disabledCheckboxMessage)}
                onChange={showFieldName =>
                  onUpdateLabelFormattingAt(index, { showFieldName })
                }
              >
                Show field name on label
              </Checkbox>
            </FlexBox>
          )}
        </DisableContainer>
        <GhostButton
          dataTestId="delete-multilabel-rule-button"
          onClick={() => onLabelFormattingDeleted(index)}
        >
          <DeleteIcon />
        </GhostButton>
      </FlexBox>
    </RuleContainer>
  );
};
export { LabelFormattingComponentHorizontal };
